import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Building2, MapPin, Globe, Mail, Phone, Target, Pencil, X, Loader2 } from 'lucide-react'
import { CommunityLayout, GovPageTitle, GovCard, GovButton } from '@/components/community/CommunityLayout'
import { useCommunity } from '@/components/community/CommunityContext'
import { Avatar } from '@/components/community/ui'
import { updateCommunity, type CommunityUpdateRequest } from '@/services/communityApi'

export default function CommunityProfilePage() {
  const { t } = useTranslation()
  const { community, isLeader, refresh } = useCommunity()
  const [editing, setEditing] = useState(false)

  if (!community) return null

  return (
    <CommunityLayout>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <GovPageTitle title={t('community.profile.title')} subtitle={t('community.profile.subtitle')} icon={Building2} />
        {isLeader && (
          <GovButton variant="secondary" size="sm" onClick={() => setEditing(true)} className="mt-1">
            <Pencil className="w-4 h-4 mr-1" aria-hidden="true" />{t('community.profile.edit')}
          </GovButton>
        )}
      </div>

      <GovCard className="mb-4">
        <div className="flex items-start gap-4 flex-wrap">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-600 to-cyan-500 flex items-center justify-center shrink-0">
            {community.logoUrl ? (
              <img src={community.logoUrl} alt="" className="w-full h-full rounded-2xl object-cover" />
            ) : (
              <Building2 className="w-8 h-8 text-white" aria-hidden="true" />
            )}
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="text-xl font-bold text-gray-900">{community.name}</h2>
            <p className="text-sm text-gray-600 mt-1">{community.description}</p>
          </div>
        </div>
      </GovCard>

      <div className="grid sm:grid-cols-2 gap-3">
        <InfoRow icon={MapPin} label={t('community.profile.location')} value={community.location} />
        <InfoRow icon={Globe} label={t('community.profile.languages')} value={community.languages?.join(', ')} />
        <InfoRow icon={Target} label={t('community.profile.focusAreas')} value={community.focusAreas?.join(', ')} />
        <InfoRow icon={Mail} label={t('community.profile.contactEmail')} value={community.contactEmail} />
        <InfoRow icon={Phone} label={t('community.profile.contactPhone')} value={community.contactPhone} />
      </div>

      {/* Leadership */}
      <h2 className="text-sm font-semibold text-gray-700 mt-6 mb-3">{t('community.profile.leadership')}</h2>
      <div className="grid sm:grid-cols-2 gap-3">
        <GovCard>
          <div className="flex items-center gap-3">
            <Avatar name={community.leader.fullName} image={community.leader.profileImage} />
            <div className="min-w-0">
              <p className="font-medium text-gray-900 truncate">{community.leader.fullName}</p>
              <p className="text-xs text-gray-500">{t('community.role.LEADER')}</p>
            </div>
          </div>
        </GovCard>
        {community.coordinator && (
          <GovCard>
            <div className="flex items-center gap-3">
              <Avatar name={community.coordinator.fullName} image={community.coordinator.profileImage} />
              <div className="min-w-0">
                <p className="font-medium text-gray-900 truncate">{community.coordinator.fullName}</p>
                <p className="text-xs text-gray-500">{t('community.role.COORDINATOR')}</p>
              </div>
            </div>
          </GovCard>
        )}
      </div>

      {/* Guidelines */}
      {community.guidelines && (
        <>
          <h2 className="text-sm font-semibold text-gray-700 mt-6 mb-3">{t('community.profile.guidelines')}</h2>
          <GovCard>
            <p className="text-sm text-gray-600 whitespace-pre-line">{community.guidelines}</p>
          </GovCard>
        </>
      )}

      {editing && <EditModal onClose={() => setEditing(false)} onSaved={async () => { await refresh(); setEditing(false) }} />}
    </CommunityLayout>
  )
}

function InfoRow({ icon: Icon, label, value }: { icon: React.ComponentType<{ className?: string }>; label: string; value?: string }) {
  if (!value) return null
  return (
    <GovCard>
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg bg-gray-50 flex items-center justify-center shrink-0">
          <Icon className="w-4 h-4 text-gray-500" aria-hidden="true" />
        </div>
        <div className="min-w-0">
          <p className="text-xs text-gray-400">{label}</p>
          <p className="text-sm text-gray-800 break-words">{value}</p>
        </div>
      </div>
    </GovCard>
  )
}

function EditModal({ onClose, onSaved }: { onClose: () => void; onSaved: () => Promise<void> }) {
  const { t } = useTranslation()
  const { community } = useCommunity()
  const [form, setForm] = useState<CommunityUpdateRequest>({
    name: community?.name ?? '',
    description: community?.description ?? '',
    location: community?.location ?? '',
    languages: community?.languages ?? [],
    focusAreas: community?.focusAreas ?? [],
    guidelines: community?.guidelines ?? '',
    contactEmail: community?.contactEmail ?? '',
    contactPhone: community?.contactPhone ?? '',
    logoUrl: community?.logoUrl ?? '',
  })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!community) return
    setSaving(true); setError(null)
    try { await updateCommunity(community.id, form); await onSaved() }
    catch (err: any) { setError(err?.response?.data?.message || t('errors.generic')) }
    finally { setSaving(false) }
  }

  const field = (k: keyof CommunityUpdateRequest, v: string | string[]) => setForm((p) => ({ ...p, [k]: v }))
  const inputCls = 'w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none'
  const csv = (arr?: string[]) => (arr ?? []).join(', ')

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label={t('community.profile.edit')}>
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <GovCard className="relative w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">{t('community.profile.edit')}</h2>
          <button onClick={onClose} aria-label={t('community.actions.cancel')} className="p-2 rounded-lg hover:bg-gray-100 min-h-[40px] min-w-[40px] flex items-center justify-center"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('community.profile.name')}</label>
            <input required value={form.name} onChange={(e) => field('name', e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('community.profile.description')}</label>
            <textarea value={form.description} onChange={(e) => field('description', e.target.value)} rows={2} className={inputCls} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('community.profile.location')}</label>
              <input value={form.location} onChange={(e) => field('location', e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('community.profile.contactEmail')}</label>
              <input value={form.contactEmail} onChange={(e) => field('contactEmail', e.target.value)} className={inputCls} />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('community.profile.languages')}</label>
            <input value={csv(form.languages)} onChange={(e) => field('languages', e.target.value.split(',').map((s) => s.trim()).filter(Boolean))} className={inputCls} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('community.profile.focusAreas')}</label>
            <input value={csv(form.focusAreas)} onChange={(e) => field('focusAreas', e.target.value.split(',').map((s) => s.trim()).filter(Boolean))} className={inputCls} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('community.profile.guidelines')}</label>
            <textarea value={form.guidelines} onChange={(e) => field('guidelines', e.target.value)} rows={4} className={inputCls} />
          </div>
          {error && <p className="text-sm text-red-600">{error}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <GovButton variant="secondary" size="sm" onClick={onClose}>{t('community.actions.cancel')}</GovButton>
            <GovButton variant="primary" size="sm" type="submit" disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> : t('community.actions.saveChanges')}
            </GovButton>
          </div>
        </form>
      </GovCard>
    </div>
  )
}